/**
 * How the loop words what an iteration decided: one sentence, shared by the
 * run's completion comment and its check run so the two never disagree, and
 * for a CI run the list of its checks with the reason given for each
 * (`describeAutoFixCiChecks`).
 */
import type {
  AutoFixLoopCiCheckOutcome,
  AutoFixLoopCiCheckRecord,
  AutoFixLoopFindingOutcome,
  AutoFixLoopFindingRecord,
} from './types';

import { truncateUtf16 } from '@/lib/utils/well-formed-unicode';
import { markdownCodeSpan } from '@/utils/markdown-code-span';
import { escapeMarkdownText } from '@/utils/markdown-text';

/** "a", "a and b", "a, b, and c". */
export function joinList(items: string[]) {
  if (items.length <= 1) {
    return items.join('');
  }
  if (items.length === 2) {
    return `${items[0]} and ${items[1]}`;
  }
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

/** Text as a markdown blockquote, every line of it: the agent's own words on the pull request. */
export function quoteMarkdown(text: string) {
  const trimmed = text.trim();
  if (!trimmed) {
    return '';
  }
  return trimmed
    .split('\n')
    .map((line) => `> ${line}`)
    .join('\n');
}

/** "1 issue", "2 issues": a count with its noun, for nouns that take a plain "s". */
export function formatCountedNoun(count: number, noun: string) {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

/** How the run names itself on the pull request. */
const ACTOR = 'Auto-fix';

/**
 * Each outcome's label for one finding and for several, in reading order: fixes first, work left
 * for later last. Each matches the verdict the finding's thread reply starts with; an undecided
 * finding gets no reply. Every outcome is named here, so a new verdict cannot drop out of the
 * tally.
 */
export const VERDICT_LABELS = {
  fixed: ['fixed', 'fixed'],
  not_valid: ['not an issue', 'not issues'],
  already_fixed: ['already fixed', 'already fixed'],
  outdated: ['no longer applies', 'no longer apply'],
  needs_discussion: ['needs your input', 'need your input'],
  undecided: ['unfinished', 'unfinished'],
} as const satisfies Record<AutoFixLoopFindingOutcome, readonly [one: string, many: string]>;

/**
 * "4 fixed, 1 not an issue, 2 need your input": each outcome present, counted and labeled
 * (`labels`, whose order is the reading order).
 */
function tallyOutcomes<Outcome extends string>(
  items: ReadonlyArray<{ outcome: Outcome }>,
  labels: Record<Outcome, readonly [one: string, many: string]>,
) {
  return (Object.entries(labels) as Array<[Outcome, readonly [string, string]]>)
    .flatMap(([outcome, [one, many]]) => {
      const count = items.filter((item) => item.outcome === outcome).length;
      return count > 0 ? [`${count} ${count === 1 ? one : many}`] : [];
    })
    .join(', ');
}

/** Whether the run reached a verdict on at least one of its findings, or of its failing checks. */
export function decidedAnyOutcome(items: ReadonlyArray<{ outcome: string }>) {
  return items.some((item) => item.outcome !== 'undecided');
}

/**
 * What a run decided about its findings or its failing checks, tallied with `labels`, without the
 * link: the comment's text and the check run's title.
 */
function describeAutoFixIteration<Outcome extends string>(params: {
  success: boolean;
  items: ReadonlyArray<{ outcome: Outcome }>;
  labels: Record<Outcome, readonly [one: string, many: string]>;
  needsEnvironmentSetup?: boolean;
}) {
  const tally = decidedAnyOutcome(params.items)
    ? ` ${tallyOutcomes(params.items, params.labels)}.`
    : '';
  if (params.needsEnvironmentSetup) {
    return `${ACTOR} is blocked. Please configure this repository’s environment to continue.${tally}`;
  }
  if (!params.success) {
    return `${ACTOR} hit an error.${tally}`;
  }
  if (params.items.length === 0) {
    return `${ACTOR} made no changes.`;
  }
  return `${ACTOR}: ${tallyOutcomes(params.items, params.labels)}.`;
}

/** What the run decided, without the link: the comment's text and the check run's title. */
export function describeAutoFixLoopIteration(params: {
  success: boolean;
  findings: ReadonlyArray<Pick<AutoFixLoopFindingRecord, 'outcome'>>;
  /** The run stopped for its repository's environment setup, which it says first. */
  needsEnvironmentSetup?: boolean;
}) {
  return describeAutoFixIteration({
    success: params.success,
    items: params.findings,
    labels: VERDICT_LABELS,
    needsEnvironmentSetup: params.needsEnvironmentSetup,
  });
}

/**
 * Each CI outcome's label for one check and for several, in reading order: fixes first, checks
 * left for a person last, as `VERDICT_LABELS` reads for findings. Every outcome is named here, so
 * a new verdict cannot drop out of the tally.
 */
const CI_VERDICT_LABELS = {
  fixed: ['check fixed', 'checks fixed'],
  flaky: ['flaky check', 'flaky checks'],
  infrastructure: ['check failed on CI infrastructure', 'checks failed on CI infrastructure'],
  pre_existing: ['check fails without this pull request', 'checks fail without this pull request'],
  needs_discussion: ['check needs your input', 'checks need your input'],
  undecided: ['check unfinished', 'checks unfinished'],
} as const satisfies Record<AutoFixLoopCiCheckOutcome, readonly [one: string, many: string]>;

/** What a CI run decided, without the link: the comment's text and the check run's title. */
export function describeAutoFixCiIteration(params: {
  success: boolean;
  checks: ReadonlyArray<Pick<AutoFixLoopCiCheckRecord, 'outcome'>>;
  /** The run stopped for its repository's environment setup, which it says first. */
  needsEnvironmentSetup?: boolean;
}) {
  return describeAutoFixIteration({
    success: params.success,
    items: params.checks,
    labels: CI_VERDICT_LABELS,
    needsEnvironmentSetup: params.needsEnvironmentSetup,
  });
}

/** How the list names each CI outcome. */
const CI_OUTCOME_LABEL = {
  fixed: 'Fixed.',
  flaky: 'Flaky.',
  infrastructure: 'Infrastructure.',
  pre_existing: 'Fails without this pull request.',
  needs_discussion: 'Needs your input.',
  undecided: 'Not finished.',
} as const satisfies Record<AutoFixLoopCiCheckOutcome, string>;

/** Room for one reason: a few sentences, never a pasted log. */
const MAX_CI_REASON_CHARS = 600;

/** Check names are short; a longer one is cut so its line cannot crowd out the others. */
const MAX_CI_CHECK_NAME_CHARS = 200;

/**
 * The longest list the comment and the check run carry: under GitHub's
 * 65,535-character cap on a check run's summary and a comment's body, with
 * room left for the tally and the progress link around it.
 */
const MAX_CI_CHECK_LIST_CHARS = 60_000;

/**
 * `text` on one line, whitespace collapsed, cut to `maxChars` with a visible ellipsis. The cut
 * never splits a character's surrogate pair (`truncateUtf16`).
 */
function toBoundedLine(text: string, maxChars: number) {
  const line = text.replace(/\s+/g, ' ').trim();
  return line.length > maxChars ? `${truncateUtf16(line, maxChars - 1).trimEnd()}…` : line;
}

const CI_OUTCOME_ORDER = Object.keys(CI_VERDICT_LABELS);

function describeCiCheck(
  check: Pick<AutoFixLoopCiCheckRecord, 'name' | 'outcome' | 'reason' | 'commitShas'>,
) {
  // An unfinished check's reason is the loop's own, often the run's internal
  // error, and the check comes back: its line says only that it is not done.
  // The reason is the model's, written after reading the pull request's CI
  // logs, so it is shown as literal text: no mentions, links, or markup.
  const reason =
    check.outcome === 'undecided'
      ? ''
      : escapeMarkdownText(toBoundedLine(check.reason, MAX_CI_REASON_CHARS));
  const commits = check.commitShas?.length
    ? `(${check.commitShas.map((sha) => sha.slice(0, 7)).join(', ')})`
    : '';
  const name = markdownCodeSpan(toBoundedLine(check.name, MAX_CI_CHECK_NAME_CHARS));
  return [`- ${name}: ${CI_OUTCOME_LABEL[check.outcome]}`, reason, commits]
    .filter(Boolean)
    .join(' ');
}

/**
 * What a CI run decided about each failing check, as a markdown list in the
 * tally's order, one line per check with the reason the run gave: that reason
 * (or question) is how the author learns why a check was left to them, so the
 * comment and the check run both carry it. Lines that would pass `maxChars`
 * are dropped from the end with a note of how many. Empty with no check.
 */
export function describeAutoFixCiChecks(
  checks: ReadonlyArray<
    Pick<AutoFixLoopCiCheckRecord, 'name' | 'outcome' | 'reason' | 'commitShas'>
  >,
  maxChars = MAX_CI_CHECK_LIST_CHARS,
) {
  const lines = [...checks]
    .sort((a, b) => CI_OUTCOME_ORDER.indexOf(a.outcome) - CI_OUTCOME_ORDER.indexOf(b.outcome))
    .map(describeCiCheck);
  for (let shown = lines.length; shown >= 0; shown--) {
    const hidden = lines.length - shown;
    const list = [
      ...lines.slice(0, shown),
      ...(hidden > 0 ? [`- ${formatCountedNoun(hidden, 'more check')} not shown.`] : []),
    ].join('\n');
    if (list.length <= maxChars) {
      return list;
    }
  }
  return '';
}
